import PopupSwitcher from "@components/Switchers/PopupSwitcher/PopupSwitcher"
import clsx from "clsx"
import {AnimatePresence, motion} from "framer-motion"
import Link from "next/link"
import {useRouter} from "next/router"
import React, {useLayoutEffect, useRef, useState} from "react"

import styles from "./AnimatedButton.module.scss"

interface PopupProps {
	data: any
	type: "content" | "asset" | "default"
}

interface AnimatedButtonProps {
	text: string
	href?: string
	onClick?: () => void
	variant?: "primary" | "text" | "secondary"
	contrast?: "light" | "dark"
	theme?: "oneHome" | "oneDefault" | "studio"
	className?: string
	disablePaddingHorizontalClass?: string
	popup?: PopupProps
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
	text,
	href,
	onClick,
	variant = "primary",
	contrast = "light",
	className = "",
	theme,
	disablePaddingHorizontalClass,
	popup,
}) => {
	const isFirstRender = useRef(true)
	const measureRef = useRef<HTMLSpanElement>(null)
	const [width, setWidth] = useState<number | null>(null)

	// Popup state
	const [showPopup, setShowPopup] = useState(false)
	const togglePopup = () => setShowPopup((prev) => !prev)

	useLayoutEffect(() => {
		if (measureRef.current) {
			const {width: textWidth} =
				measureRef.current.getBoundingClientRect()
			setWidth(textWidth)
		}
	}, [text])

	const buttonClasses = clsx(
		"buttonBase",
		styles.buttonBase,
		styles[variant],
		contrast === "dark" && styles.contrast,
		theme && styles[theme],
		className,
		disablePaddingHorizontalClass,
	)

	const content = (
		<>
			<span
				ref={measureRef}
				className={clsx(buttonClasses)}
				style={{
					position: "absolute",
					visibility: "hidden",
					whiteSpace: "nowrap",
					padding: "0 32px",
					fontSize: "16px",
					fontWeight: 600,
					pointerEvents: "none",
				}}
			>
				{text}
			</span>
			{width !== null && (
				<motion.div
					animate={{width}}
					initial={false}
					transition={{
						duration: isFirstRender.current ? 0 : 0.3,
						ease: "easeInOut",
					}}
					className={clsx(styles.buttonBase, buttonClasses)}
					onAnimationComplete={() => {
						isFirstRender.current = false
					}}
					style={{
						overflow: "hidden",
						display: "inline-block",
						position: "relative",
						transformOrigin: "left",
						height: "100%",
					}}
				>
					<AnimatePresence mode={"sync"}>
						<motion.span
							key={text}
							initial={{opacity: isFirstRender.current ? 1 : 0}}
							animate={{opacity: 1}}
							exit={{opacity: 0}}
							transition={{
								duration: isFirstRender.current ? 0 : 0.25,
								ease: "easeInOut",
							}}
							onAnimationComplete={() => {
								isFirstRender.current = false
							}}
							style={{
								position: "absolute",
								top: "50%",
								left: "32px",
								transform: "translateY(-50%)",
								whiteSpace: "nowrap",
							}}
						>
							{text}
						</motion.span>
					</AnimatePresence>
				</motion.div>
			)}
		</>
	)

	const router = useRouter()

	if (href) {
		const isAnchor = href.startsWith("#")
		const [hrefPath, hash] = href.split("#") || []

		const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
			if (isAnchor && (hrefPath === "" || hrefPath === router.pathname)) {
				e.preventDefault()
				const target = document.getElementById(hash)
				if (target) {
					target.scrollIntoView({behavior: "smooth"})
					history.replaceState(null, "", `#${hash}`)
				}
				return
			}

			if (isAnchor && hrefPath !== router.pathname) {
				sessionStorage.setItem("scrollTarget", hash)
			}
		}

		const targetExternal = href?.includes("http") || href?.includes("www")

		return (
			<Link
				target={targetExternal ? "_blank" : ""}
				href={href}
				passHref
				legacyBehavior
				scroll={false}
				rel={"noreferrer"}
			>
				<a
					target={targetExternal ? "_blank" : ""}
					style={{display: "inline-block", position: "relative"}}
					onClick={handleAnchorClick}
				>
					{content}
				</a>
			</Link>
		)
	}

	return (
		<>
			<button
				onClick={onClick ?? (popup ? togglePopup : undefined)}
				type={"button"}
				style={{all: "unset", position: "relative"}}
			>
				{content}
			</button>

			{/* Conditional popup rendering */}
			{popup && showPopup && (
				<PopupSwitcher
					data={popup.data}
					handleClick={togglePopup}
					type={popup.type}
				/>
			)}
		</>
	)
}

export default AnimatedButton
